'use client'

import { isAbortError } from '@hello-aria/makalu-api-client'
import { isEmpty, isNil } from 'lodash'
import { useTranslations } from 'next-intl'

import { In, Nil, NotNil } from '@hello-aria/query-string'
import type { FilterParamsInput, QueryStringInput } from '@hello-aria/query-string/dist/stringify'

import type { Loan, LoanStatus as LoanStatusEnum, LoanStatusToDisplay } from '@/shared/models/loanModels'
import { loanStatusesToDisplay } from '@/shared/models/loanModels'

import { useExport } from '@/modules/export/hooks/useExports'
import { buildExportQuery, prepareAndDownloadExportFile } from '@/modules/export/utils/exportUtils'
import BillingDocumentCell from '@/modules/home/components/BillingDocumentCell'
import { useRouter } from '@/navigation'
import TableDropdownActions from '@/shared/components/common/Dropdown/TableDropdownActions'
import { useAvailableCurrencies, useCompany } from '@/shared/hooks/useCompanies'
import { useUnlinkReceivableFromInvoice } from '@/shared/hooks/useInvoices'
import { useInfiniteLoans, useInvalidateLoans } from '@/shared/hooks/useLoans'
import { useFiltersBuilder, useQueryBuilder } from '@/shared/hooks/useQueryParams'
import { useInfiniteReceivables } from '@/shared/hooks/useReceivables'
import { useSetToast } from '@/shared/hooks/useSetToast'
import { useInfiniteUsers } from '@/shared/hooks/useUsers'
import type { UnlinkReceivable } from '@/shared/models/invoiceModels'
import type { Currency } from '@/shared/models/moneyModel'
import type { SortingRule } from '@/shared/models/queryModels'
import type { Receivable } from '@/shared/models/receivableModels'
import type { User } from '@/shared/models/userModel'
import { getDebtorDisplayName } from '@/shared/utils/debtorsUtils'
import { translateJSON } from '@/shared/utils/filesUtils'
import { flattenInfiniteData } from '@/shared/utils/queryUtils'
import { getReceivableActionPermissions } from '@/shared/utils/receivableUtils'
import { resolvePath } from '@/shared/utils/routesUtils'
import UnlinkReceivablesDialog from '@/shared/v2/loans/UnlinkReceivablesDialog'
import AmountWithCurrency from '@/shared/v2/shared/components/AmountWithCurrency'
import CautionBadge from '@/shared/v2/shared/components/CautionBadge'
import DropdownMenu from '@/shared/v2/shared/components/DropDownMenu'
import type { ExportData } from '@/shared/v2/shared/components/ExportDataDialog'
import ExportDataDialog from '@/shared/v2/shared/components/ExportDataDialog'
import { FilterSelect } from '@/shared/v2/shared/components/FilterSelect'
import LoanStatus from '@/shared/v2/shared/components/LoanStatus'
import Paragraph from '@/shared/v2/shared/components/Paragraph'
import PrivilegesButton from '@/shared/v2/shared/components/PrivilegesButton'
import SearchInput from '@/shared/v2/shared/components/SearchInput'
import type { TableProps } from '@/shared/v2/shared/components/Table'
import Table, { SMALL_COLUMN_WIDTH, XSMALL_COLUMN_WIDTH } from '@/shared/v2/shared/components/Table'
import FiltersWrapper from '@/shared/v2/shared/components/FiltersWrapper'
import { differenceInDays } from 'date-fns'
import { useState } from 'react'
import ExportDataToaster from '@/shared/v2/shared/components/ExportDataToaster'

const createLoansFilters = ({
    status,
    currency,
    hasReceivableInvoice: hasInvoice
}: {
    status: LoanStatusToDisplay[] | undefined | null
    currency: Currency[] | undefined | null
    hasReceivableInvoice: 'true' | 'false' | undefined | null
}): QueryStringInput['filters'] | undefined => {
    if (isNil(status) && isNil(currency) && isNil(hasInvoice)) {
        return
    }

    const filters: FilterParamsInput[] = []

    if (status?.length) {
        filters.push(In('status', status))
    }

    if (currency?.length) {
        filters.push(In('currency', currency))
    }

    if (hasInvoice === 'true') {
        filters.push(NotNil('invoiceId'))
    } else if (hasInvoice === 'false') {
        filters.push(Nil('invoiceId'))
    }

    return filters
}

const shouldDisplayInvoiceWarning = (loanStatus: LoanStatusEnum) => {
    const inelligibleStatuses: LoanStatusEnum[] = [
        'CANCELED',
        'PAYMENT_CANCELED',
        'REFUSED',
        'PENDING_REFUSED',
        'PAYMENT_FAILED'
    ]
    return !inelligibleStatuses.includes(loanStatus)
}

const HomePage = () => {
    const rootTranslations = useTranslations('root')
    const toasterTranslations = useTranslations('toasters')
    const setToast = useSetToast()
    const router = useRouter()

    const [selectedLoans, setSelectedLoans] = useState<Loan[]>([])
    const [receivablesToUnlink, setReceivablesToUnlink] = useState<UnlinkReceivable[]>([])

    const exportLoans = useExport('loans')
    const company = useCompany()
    const availableCurrencies = useAvailableCurrencies(company.data)

    const unlinkReceivableFromInvoice = useUnlinkReceivableFromInvoice()
    const { invalidateLoans } = useInvalidateLoans()

    const filters = useFiltersBuilder('loans', {
        status: {
            type: 'multi',
            items: loanStatusesToDisplay,
            getLabel: (status) => rootTranslations(`components.filters.status.loan.${status}`)
        },
        currency: {
            type: 'multi',
            items: availableCurrencies,
            getLabel: (currency) => rootTranslations(`components.filters.currency.${currency}`)
        },
        hasReceivableInvoice: {
            type: 'single',
            items: ['true', 'false'] as const,
            getLabel: (hasInvoice) =>
                rootTranslations(`components.loansTable.filters.hasReceivableInvoice.${hasInvoice}`)
        }
    })

    const { query, search, sort, handleSearch, handleSort } = useQueryBuilder({
        namespace: 'loans',
        filters: createLoansFilters({
            status: filters.status.value,
            currency: filters.currency.value,
            hasReceivableInvoice: filters.hasReceivableInvoice.value?.[0]
        })
    })

    const loans = useInfiniteLoans({
        query
    })

    const flattenLoans = flattenInfiniteData(loans?.data)
    const receivableIds =
        flattenLoans
            ?.map((loan) => loan.receivableId)
            .filter((receivableId): receivableId is string => !isNil(receivableId)) ?? []

    const receivables = useInfiniteReceivables({
        query: {
            filters: !isEmpty(receivableIds) ? [In('id', receivableIds)] : []
        },
        isEnabled: !isEmpty(receivableIds)
    })

    const flattenReceivables = flattenInfiniteData(receivables?.data)

    const userIds = flattenLoans?.map((loan) => loan.userId).filter((userId): userId is string => !isNil(userId)) ?? []

    const users = useInfiniteUsers({
        enabled: !isEmpty(userIds),
        query: {
            filters: !isEmpty(userIds) ? [In('id', userIds)] : []
        }
    })
    const flattenUsers = flattenInfiniteData(users?.data)

    const onSearch = (value: string) => {
        handleSearch(value)
    }

    const onSortBy = (newSorting: SortingRule<Loan>[]) => {
        handleSort(newSorting)
    }

    const onExportLoans = async (payload: ExportData) => {
        try {
            const baseQuery = buildExportQuery(payload)
            const exportQuery = {
                ...query,
                filters: [...(query.filters || []), ...(baseQuery || [])]
            }

            let response

            if (payload.scope === 'withoutFilters') {
                response = await exportLoans.mutateAsync({
                    query: {
                        filters: baseQuery
                    }
                })
            } else {
                response = await exportLoans.mutateAsync({ query: exportQuery })
            }

            const translatedFile = translateJSON(response, {
                valueTranslation: {},
                keyTranslation: (value) => rootTranslations(`components.exportDialog.columns.${value}`)
            })

            return prepareAndDownloadExportFile(translatedFile, 'loans', payload)
        } catch (error) {
            if (isAbortError(error)) {
                return
            }

            setToast({
                intent: 'error',
                description: toasterTranslations('export.negative.description'),
                title: toasterTranslations('export.negative.title')
            })
        }
    }

    const onCancelExportLoans = () => {
        exportLoans.cancel()
    }

    const findUserById = (userId: string | undefined): User | undefined => {
        return flattenUsers?.find((user) => user.id === userId)
    }

    const findReceivableById = (receivableId: string | null | undefined): Receivable | undefined => {
        return flattenReceivables?.find((receivable) => receivable.id === receivableId)
    }

    const columns: TableProps<Loan>['columns'] = [
        {
            id: 'createdAt',
            header: rootTranslations('components.loansTable.columns.createdAt'),
            data: {
                value: 'createdAt'
            },
            cellType: 'date',
            size: XSMALL_COLUMN_WIDTH,
            enableSorting: true
        },
        {
            id: 'invoice',
            header: rootTranslations('components.loansTable.columns.billingDocument'),
            cell: ({ row }) => {
                const receivable = findReceivableById(row.original.receivableId)
                return (
                    <BillingDocumentCell
                        receivableReference={receivable?.reference}
                        invoiceNumber={row.original.invoice?.invoiceNumber}
                    />
                )
            },
            cellType: 'text',
            size: SMALL_COLUMN_WIDTH
        },
        {
            id: 'amount',
            header: rootTranslations('components.loansTable.columns.amount'),
            cell: ({ row }) => (
                <AmountWithCurrency type="cell" amount={row.original.amount} currency={row.original.currency} />
            ),
            enableSorting: true
        },
        {
            id: 'beneficiary.businessName',
            header: rootTranslations('components.loansTable.columns.beneficiary'),
            cell: ({ row }) => {
                const user = findUserById(row.original.userId)

                if (user) return user.businessName ?? user.email
                else return '-'
            },
            cellType: 'text'
        },
        {
            id: 'invoice.debtorName',
            header: rootTranslations('components.loansTable.columns.debtor'),
            cell: ({ row }) => {
                const isReceivable = !isNil(row.original.receivableId)
                if (isReceivable) {
                    const receveivable = findReceivableById(row.original.receivableId)
                    return getDebtorDisplayName(receveivable?.debtor?.name, receveivable?.debtorIdentifier)
                }
                return getDebtorDisplayName(row.original.invoice?.debtorName, row.original.invoice?.debtorIdentifier)
            },
            cellType: 'text',
            enableSorting: true,
            size: SMALL_COLUMN_WIDTH
        },
        {
            id: 'status',
            header: rootTranslations('components.loansTable.columns.status'),
            cell: ({ row }) => {
                const loanReceivable = findReceivableById(row.original.receivableId)
                const isDueSoon = loanReceivable?.invoiceIsDueSoon
                const isOverdue = loanReceivable?.invoiceIsOverdue
                const loanStatus = row.original.status
                if (shouldDisplayInvoiceWarning(loanStatus)) {
                    return (
                        <div className="flex gap-2">
                            <LoanStatus status={row.original.status} />
                            {isDueSoon ? (
                                <CautionBadge
                                    tooltip={rootTranslations('components.loansTable.invoiceWarning.dueSoon', {
                                        count: Math.abs(
                                            differenceInDays(
                                                new Date(loanReceivable.expectedInvoiceDueDate),
                                                // eslint-disable-next-line react/purity -- the count is read from the moment the page renders, as it always has
                                                new Date(Date.now())
                                            )
                                        )
                                    })}
                                    intent="warning"
                                />
                            ) : null}
                            {isOverdue ? (
                                <CautionBadge
                                    tooltip={rootTranslations('components.loansTable.invoiceWarning.overdue')}
                                    intent="negative"
                                />
                            ) : null}
                        </div>
                    )
                }
                return <LoanStatus status={row.original.status} />
            }
        },
        {
            id: 'actions',
            cell: ({ row }) => <TableDropdownActions dataToCopy={row.original.id} />,
            size: XSMALL_COLUMN_WIDTH
        }
    ]

    const onLinkReceivables = () => {
        router.push({
            pathname: resolvePath('/receivables/link'),
            query: {
                loanIds: selectedLoans.map((loan) => loan.id).join(',')
            }
        })
    }

    const { canLink, canUnlink } = isEmpty(selectedLoans)
        ? {
              canLink: {
                  enabled: false,
                  tooltip: rootTranslations('components.loansTable.tableActions.emptySelectionWarningTooltip')
              },
              canUnlink: {
                  enabled: false,
                  tooltip: rootTranslations('components.loansTable.tableActions.emptySelectionWarningTooltip')
              }
          }
        : getReceivableActionPermissions(
              selectedLoans.map((loan) => findReceivableById(loan.receivableId)),
              {
                  differentDebtorsWarningTooltip: rootTranslations(
                      'components.loansTable.tableActions.differentDebtorsWarningTooltip'
                  ),
                  differentCurrenciesWarningTooltip: rootTranslations(
                      'components.loansTable.tableActions.differentCurrenciesWarningTooltip'
                  ),
                  noReceivableWarningTooltip: rootTranslations(
                      'components.loansTable.tableActions.noReceivableWarningTooltip'
                  ),
                  receivableHasInvoiceWarningTooltip: rootTranslations(
                      'components.loansTable.tableActions.receivableHasInvoiceWarningTooltip'
                  ),
                  receivableHasNoInvoiceWarningTooltip: rootTranslations(
                      'components.loansTable.tableActions.receivableHasNoInvoiceWarningTooltip'
                  )
              }
          )

    const onCloseUnlinkReceivableDialog = () => {
        setReceivablesToUnlink([])
    }

    const onOpenUnlinkReceivableDialog = () => {
        setReceivablesToUnlink(
            selectedLoans.map((loan) => ({
                invoiceId: findReceivableById(loan.receivableId)?.invoiceId,
                currentReceivableIds: loan.invoice?.receivableIds ?? [],
                receivableIdToUnlink: loan.receivableId
            }))
        )
    }

    const onUnlinkReceivables = async () => {
        try {
            await Promise.all(receivablesToUnlink.map((payload) => unlinkReceivableFromInvoice.mutateAsync(payload)))

            onCloseUnlinkReceivableDialog()
            await invalidateLoans()
            setToast({
                title: rootTranslations('components.loansTable.unlinkReceivableInvoice.toaster.success.title'),
                description: rootTranslations(
                    'components.loansTable.unlinkReceivableInvoice.toaster.success.description'
                ),
                intent: 'success'
            })
            setSelectedLoans([])
        } catch (error) {
            onCloseUnlinkReceivableDialog()
            await invalidateLoans()
            setToast({
                title: rootTranslations('components.loansTable.unlinkReceivableInvoice.toaster.error.title'),
                description: rootTranslations(
                    'components.loansTable.unlinkReceivableInvoice.toaster.error.description'
                ),
                intent: 'error'
            })
            setSelectedLoans([])
        }
    }

    const shouldDisplayReceivableActions = company.data?.config?.receivable?.enabled

    return (
        <div className="grid grid-cols-1 gap-y-3">
            <ExportDataToaster isMutating={exportLoans.isMutating} onCancelExport={onCancelExportLoans} />

            {shouldDisplayReceivableActions ? (
                <UnlinkReceivablesDialog
                    receivablesToUnlink={receivablesToUnlink}
                    onSubmit={onUnlinkReceivables}
                    onClose={onCloseUnlinkReceivableDialog}
                    isLoading={unlinkReceivableFromInvoice.isPending}
                />
            ) : null}

            <div className="flex items-center justify-between gap-4">
                <SearchInput
                    data-testid="__table-actions-search-bar__"
                    placeholder={rootTranslations('components.searchBarPlaceholder')}
                    value={search}
                    onChange={onSearch}
                />
                <ExportDataDialog
                    namespace="loans"
                    status={exportLoans.status}
                    isMutating={exportLoans.isMutating}
                    onSubmit={onExportLoans}
                />
            </div>

            <FiltersWrapper alignLastRight={shouldDisplayReceivableActions}>
                <FilterSelect
                    data-testid="__loans-status-filter__"
                    filter={filters.status}
                    placeholder={rootTranslations('components.filters.status.placeholder')}
                />

                <FilterSelect
                    data-testid="__loans-currency-filter__"
                    filter={filters.currency}
                    placeholder={rootTranslations('components.filters.currency.placeholder')}
                />

                {shouldDisplayReceivableActions && (
                    <FilterSelect
                        data-testid="__loans-receivable-receivable-invoice-filter__"
                        filter={filters.hasReceivableInvoice}
                        placeholder={rootTranslations('components.loansTable.filters.hasReceivableInvoice.all')}
                    />
                )}

                {shouldDisplayReceivableActions ? (
                    <div data-testid="__loans-actions-dropdown__" className="flex gap-2 items-center justify-end">
                        {selectedLoans?.length > 0 && (
                            <Paragraph data-testid="__loans-actions-selection-count__" className="whitespace-nowrap">
                                {rootTranslations('components.loansTable.tableActions.selectionCountBadge', {
                                    count: selectedLoans.length
                                })}
                            </Paragraph>
                        )}
                        <DropdownMenu
                            modal={true}
                            trigger={
                                <PrivilegesButton
                                    requiredPrivilege="readwrite"
                                    intent={'secondary'}
                                    data-testid="__loans-actions-dropdown-trigger__"
                                    className="whitespace-nowrap font-sans text-[14px]"
                                >
                                    {rootTranslations('components.loansTable.tableActions.trigger')}
                                </PrivilegesButton>
                            }
                            items={[
                                {
                                    label: rootTranslations('components.loansTable.tableActions.actions.link'),
                                    action: () => onLinkReceivables(),
                                    isDisabled: !canLink.enabled,
                                    tooltip: canLink?.tooltip
                                },
                                {
                                    label: rootTranslations('components.loansTable.tableActions.actions.unlink'),
                                    action: () => onOpenUnlinkReceivableDialog(),
                                    isDisabled: !canUnlink.enabled,
                                    tooltip: canUnlink?.tooltip
                                }
                            ]}
                        />
                    </div>
                ) : null}
            </FiltersWrapper>

            <Table<Loan>
                emptyState={rootTranslations('components.loansTable.emptyData')}
                align={{ right: ['amount', 'actions'] }}
                isLoading={loans.isLoading}
                columns={columns}
                data={flattenLoans}
                loadMore={{
                    fetchNextPage: loans.fetchNextPage,
                    hasNextPage: loans.hasNextPage,
                    isFetchingNextPage: loans.isFetchingNextPage
                }}
                selectedRows={selectedLoans}
                onSortBy={onSortBy}
                getRowHref={(row) => resolvePath(`/loans/${row.id}`)}
                {...(shouldDisplayReceivableActions
                    ? {
                          onRowSelectionChange: (selectedRows) => {
                              setSelectedLoans(selectedRows)
                          }
                      }
                    : {})}
                initialSortBy={sort ?? undefined}
            />
        </div>
    )
}

export default HomePage
